import { zodResolver } from '@hookform/resolvers/zod';
import {
    Alert, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, Typography,
} from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useRef } from 'react';
import { VariableChips, insertAtCursor } from '@components/MessageField/variable-chips';
import { z } from 'zod';
import { useEffect } from 'react';
import { Form } from '@components/Form';
import MDButton from '@components/MDButton';
import { smallDialog } from 'assets/theme/dialogSize';
import { setLoading, useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { QuickReplyService } from '../services/quick-reply.service';
import { QuickReplyRequest } from '../services/requests/quick-reply.request';
import { QuickReplyResponse } from '../services/responses/quick-reply.response';
import { firstErrorMessage } from '@functions/first-error-message';

interface Props {
    open: boolean;
    edit: QuickReplyResponse | null;
    handleModal: () => void;
    getAll: () => void;
    clearSearch: () => void;
}

export const QuickReplyModal = ({ open, edit, handleModal, getAll, clearSearch }: Props) => {
    const { t } = useTranslation();
    const [, dispatch] = useMaterialUIController();
    const { showAlert } = useAlert();
    const service = new QuickReplyService();

    const schema = z.object({
        shortcut: z
            .string()
            .min(1, t('quickReplies.atalhoObrigatorio'))
            // A barra é acrescentada ao gravar: o limite conta com ela, e só a barra não é atalho.
            .refine((value) => `/${value.trim().replace(/^\/+/, '')}`.length <= 40, t('quickReplies.atalhoLongo'))
            .refine((value) => value.trim().replace(/^\/+/, '').length > 0, t('quickReplies.atalhoObrigatorio'))
            .refine((value) => !/\s/.test(value), t('quickReplies.atalhoSemEspaco')),
        content: z
            .string()
            .min(1, t('quickReplies.conteudoObrigatorio'))
            .max(2000, t('quickReplies.conteudoLongo')),
    });

    const form = useForm<QuickReplyRequest>({
        resolver: zodResolver(schema),
        defaultValues: { shortcut: '', content: '' },
    });

    const { handleSubmit, reset, setValue, getValues, formState: { errors } } = form;

    /// A marcação entra onde está o cursor, não no fim.
    const contentRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);

    const insertVariable = (key: string) =>
        setValue(
            'content',
            insertAtCursor(contentRef.current, getValues('content') ?? '', key),
            { shouldDirty: true }
        );

    useEffect(() => {
        if (!open) return;

        reset(edit ? { shortcut: edit.shortcut, content: edit.content } : { shortcut: '', content: '' });
    }, [open]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            showAlert(firstErrorMessage(errors) ?? t('general.camposObrigatorios'), 'error', 2500);
        }
    }, [errors]);

    const saveChanges = async (data: QuickReplyRequest) => {
        setLoading(dispatch, true);

        try {
            if (edit) {
                await service.update(edit.id, data);
                showAlert(t('general.atualizacao'), 'success', 2500);
                getAll();
            } else {
                await service.create(data);
                showAlert(t('general.cadastro'), 'success', 2500);
                clearSearch();
            }

            handleModal();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    return (
        <Dialog open={open} onClose={handleModal} fullWidth maxWidth="md" scroll="paper">
            <DialogTitle>{edit ? t('general.editar') : t('general.adicionar')}</DialogTitle>
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(saveChanges)}>
                    <DialogContent style={smallDialog}>
                        <Grid container spacing={2}>
                            <Form.Field size={12}>
                                <FormControl fullWidth>
                                    <Form.Label>
                                        {t('quickReplies.atalho')}
                                        <Form.MandatoryIcon />
                                    </Form.Label>
                                    <Form.Input
                                        type="text"
                                        autoComplete="off"
                                        size="medium"
                                        name="shortcut"
                                        placeholder="/orcamento"
                                        autoFocus
                                    />
                                    <Form.ErrorMessage field="shortcut" />
                                </FormControl>
                            </Form.Field>

                            <Form.Field size={12}>
                                <FormControl fullWidth>
                                    <Form.Label>
                                        {t('quickReplies.conteudo')}
                                        <Form.MandatoryIcon />
                                    </Form.Label>
                                    <Form.TextFields
                                        name="content"
                                        multiline
                                        minRows={7}
                                        size="small"
                                        inputRef={contentRef}
                                        inputProps={{ maxLength: 2000 }}
                                    />
                                    <Form.ErrorMessage field="content" />

                                    {/* A resposta entra pronta na caixa do atendente: aqui só vale marcação que a tela resolve. */}
                                    <VariableChips onInsert={insertVariable} size="large" />
                                </FormControl>
                            </Form.Field>

                            <Form.Field size={12}>
                                <Alert severity="info" icon={false}>
                                    <Typography variant="caption">
                                        {t('quickReplies.dicaVariavel')}
                                    </Typography>
                                </Alert>
                            </Form.Field>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <MDButton
                            sx={{ marginRight: '5px' }}
                            variant="gradient"
                            color="dark"
                            onClick={handleModal}
                        >
                            {t('general.cancelar')}
                        </MDButton>
                        <MDButton variant="gradient" color="success" type="submit">
                            {t('general.salvar')}
                        </MDButton>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};
